import { buildManifest, type BrandProfile } from '@pokerroom/branding';

/** Keep the generated static manifest unless the same-origin API is available. */
export async function selectRuntimeManifest(
  app: 'web' | 'dealer',
  profile: BrandProfile,
): Promise<void> {
  const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!link) return;
  const fallback = link.href;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 2000);
  const url = `/api/branding/manifest/${app}`;
  try {
    const response = await fetch(url, {
      signal: controller.signal,
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (
      !response.ok ||
      !response.headers.get('content-type')?.includes('application/manifest+json')
    )
      return;
    const manifest = await response.json();
    // A mismatched API must not silently rename the installed frontend profile.
    const expected = buildManifest(profile, app);
    if (
      manifest.name !== expected.name ||
      manifest.id !== expected.id ||
      manifest.scope !== expected.scope ||
      manifest.start_url !== expected.start_url
    )
      return;
    link.href = url;
    link.onerror = () => {
      link.href = fallback;
      link.onerror = null;
    };
  } catch {
    /* Static offline fallback stays selected. */
  } finally {
    clearTimeout(timeout);
  }
}

export function brandingUrl(key: string, updatedAt?: string): string | null {
  return updatedAt ? `/api/branding/${key}/file?v=${encodeURIComponent(updatedAt)}` : null;
}

export function applyFavicon(url: string, fallback: string): () => void {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) return () => {};
  const image = new Image();
  let active = true;
  link.href = fallback;
  image.onload = () => {
    if (active) link.href = url;
  };
  image.onerror = () => {
    if (active) link.href = fallback;
  };
  image.src = url;
  return () => {
    active = false;
    image.onload = null;
    image.onerror = null;
  };
}

/** Bound print readiness, including missing images; broken images cannot stall printing. */
export async function waitForReceiptImages(root: ParentNode = document): Promise<void> {
  await Promise.all(
    Array.from(root.querySelectorAll<HTMLImageElement>('[data-receipt] img')).map((image) => {
      if (image.complete) return Promise.resolve();
      return new Promise<void>((resolve) => {
        const finish = () => {
          clearTimeout(timeout);
          image.removeEventListener('load', finish);
          image.removeEventListener('error', finish);
          resolve();
        };
        const timeout = setTimeout(finish, 2000);
        image.addEventListener('load', finish);
        image.addEventListener('error', finish);
      });
    }),
  );
}
